import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Upload, Download, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const TIPOS = [
  { key: "choferes", get label() { return t("Choferes"); } },
  { key: "clientes", get label() { return t("Clientes"); } },
  { key: "ubicaciones", get label() { return t("Ubicaciones"); } },
];

export default function Importar() {
  const [tipo, setTipo] = useState("choferes");
  return (
    <div className="p-4 lg:p-8 max-w-3xl space-y-5">
      <div>
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Importar catálogos")}</h1>
        <ol className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[13.5px] text-ink-2">{[t("Plantilla"), t("Subir"), t("Revisar e importar")].map((p, i) => <li key={p} className="flex items-center gap-2"><span className="h-6 w-6 rounded-full bg-ink text-white font-display font-bold text-[12.5px] flex items-center justify-center">{i + 1}</span>{p}</li>)}</ol>
        <p className="text-ink-3 mt-2">{t("Sube un archivo .csv o .xlsx para crear choferes, clientes o ubicaciones en lote.")}</p>
      </div>
      <Tabs value={tipo} onValueChange={setTipo}>
        <TabsList className="bg-paper">
          {TIPOS.map((tt) => <TabsTrigger key={tt.key} value={tt.key} data-testid={`importar-tab-${tt.key}`}>{tt.label}</TabsTrigger>)}
        </TabsList>
        {TIPOS.map((tt) => (
          <TabsContent key={tt.key} value={tt.key}>
            <ImportarPanel tipo={tt.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function ImportarPanel({ tipo }) {
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const fileRef = React.useRef(null);

  const descargarPlantilla = async () => {
    try {
      const res = await api.get(`/importar/${tipo}/plantilla`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = t("plantilla_{tipo}.csv", { tipo });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const onArchivo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setArchivo(f);
    setPreview(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/importar/${tipo}?confirmar=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
      setArchivo(null);
    } finally { setLoading(false); }
  };

  const confirmar = async () => {
    if (!archivo) return;
    setConfirmando(true);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      const { data } = await api.post(`/importar/${tipo}?confirmar=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Se crearon {creadas} registro(s)", { creadas: data.creadas }));
      setArchivo(null);
      setPreview(null);
    } catch (err) { toast.error(apiError(err)); } finally { setConfirmando(false); }
  };

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" data-testid={`importar-plantilla-${tipo}`} className="h-10" onClick={descargarPlantilla}>
          <Download className="h-4 w-4 mr-2" />{t(" Descargar plantilla")}</Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={onArchivo} />
        <Button type="button" data-testid={`importar-elegir-${tipo}`} className="h-10 bg-brand hover:bg-brand-dark text-white" onClick={() => fileRef.current?.click()}>
          <Upload className="h-4 w-4 mr-2" />{t(" Elegir archivo (.csv / .xlsx)")}</Button>
        {archivo && <span className="text-sm text-ink-2">{archivo.name}</span>}
      </div>

      {loading && <div className="flex items-center gap-2 text-ink-3 text-sm"><Loader2 className="h-4 w-4 animate-spin" />{t(" Leyendo archivo…")}</div>}

      {preview && (
        <div className="space-y-3" data-testid={`importar-preview-${tipo}`}>
          <div className="flex items-center gap-4 text-sm font-semibold">
            <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />{t("  {validas} válidas", { validas: preview.validas })}</span>
            <span className="text-danger flex items-center gap-1"><XCircle className="h-4 w-4" />{t("  {invalidas} con error", { invalidas: preview.invalidas })}</span>
          </div>
          <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-paper sticky top-0">
                <tr className="text-left text-ink-3 uppercase font-bold">
                  <th className="px-3 py-2">{t("Fila")}</th>
                  <th className="px-3 py-2">{t("Estado")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EFECE6]">
                {preview.filas.map((r, i) => (
                  <tr key={i} data-testid={`importar-fila-${tipo}-${i}`} className={r.ok ? "" : "bg-[#FBEDEC]"}>
                    <td className="px-3 py-2 font-mono text-ink-2">{Object.values(r.fila).filter(Boolean).join(" · ")}</td>
                    <td className="px-3 py-2">
                      {r.ok ? <span className="text-emerald-700 font-medium">{t("OK")}</span> : <span className="text-danger font-medium">{r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button data-testid={`importar-confirmar-${tipo}`} disabled={preview.validas === 0 || confirmando} className="h-11 " onClick={confirmar}>
            {confirmando ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Confirmar importación ({validas})", { validas: preview.validas })}
          </Button>
        </div>
      )}
    </div>
  );
}
